import { useState } from 'react';
import { Modal, Placeholder } from 'react-bootstrap';
import { useShallow } from 'zustand/react/shallow';
import { ParameterItemRun } from './ParameterItemRun';
import { useGlobalStore } from '@/frontend/store/GlobalStore';
import { useParameterStore } from '@/frontend/store/ParameterStore';
import { I18n } from '@/i18n/I18n';
import { CONTEXT_LOG_SUBJECT_TYPES, CONTEXT_LOG_STATUS } from '@/types/contextLogTypes';
import { Parameter } from '@/backend/models/parameter';
import { BasicButton } from '../buttons/BasicButton';
import ContextLogViewer from '../contextLogs/ContextLogViewer';
import { ContextLogInput } from '../contextLogs/ContextLogInput';

export function ParameterUpdateValueWithContextLogModal(props: { parameter: Parameter }) {
  const { parameter } = props;
  const userInfo = useGlobalStore(useShallow(state => state.userClass));
  const contextLogsForParameter = useParameterStore(useShallow((state) => state.contextLogs.filter(c => c.subjectId === parameter.id && c.subjectType === CONTEXT_LOG_SUBJECT_TYPES.PARAMETER)));
  const pendingContextLog = contextLogsForParameter.find(c => c.status === CONTEXT_LOG_STATUS.PENDING);
  const parameterForContextLog = useParameterStore(useShallow((state) => state.parameters.find(p => p.subjectId === pendingContextLog?.id)));
  const commitParameterContextLog = useParameterStore(state => state.commitParameterContextLog);
  const createParameterContextLog = useParameterStore(state => state.createParameterContextLog);
  const deleteContextLog = useParameterStore(state => state.deleteContextLog);
  const [show, setShow] = useState(false);
  const [note, setNote] = useState('');
  const [noteError, setNoteError] = useState('');
  const [userEmail, setUserEmail] = useState(userInfo?.email || '');
  const [userEmailError, setUserEmailError] = useState('');

  const onCancel = () => {
    if (pendingContextLog) {
      void deleteContextLog(pendingContextLog.id);
    }
    setShow(false);
    clearForm();
  };

  const handleConfirm = async () => {
    if (!note || !userEmail) {
      if (!note) {
        setNoteError(I18n.get('requiredField'));
      }
      if (!userEmail) {
        setUserEmailError(I18n.get('requiredField'));
      }
      return;
    }
    if (pendingContextLog) {
      void commitParameterContextLog(pendingContextLog?.id, note, userEmail);
    }
    clearForm();
    setShow(false);
  };

  const clearForm = () => {
    setNote('');
    setNoteError('');
    setUserEmail(userInfo?.email || '');
    setUserEmailError('');
  };

  if (contextLogsForParameter.length > 0 && !pendingContextLog) {
    return (
      <ContextLogViewer subjectId={parameter.id} subjectType={CONTEXT_LOG_SUBJECT_TYPES.PARAMETER} popoverPlacement='bottom' allowDelete />
    );
  }

  const overrideValueOnClick = () => {
    void createParameterContextLog(parameter.id);
    setShow(true);
  };

  return (
    <>
      <button type='button' className='btn btn-warning' style={{ fontSize: '0.75rem' }} onClick={overrideValueOnClick}>{I18n.get('overrideValue')}</button>
      <Modal show={show} onHide={onCancel} centered size='lg'>
        <Modal.Header closeButton className='bg-warning text-white'>
          <Modal.Title>{I18n.get('overrideValue')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          { parameterForContextLog ? <ParameterItemRun parameter={parameterForContextLog} alwaysEnabled /> :
            <Placeholder className='d-flex flex-column' animation='wave'>
              <Placeholder.Button xs={12} size='lg' className='btn btn-secondary' outline='true' />
            </Placeholder>
          }
          <br />
          <ContextLogInput note={note} setNote={setNote} noteError={noteError} userEmail={userEmail} setUserEmail={setUserEmail} userEmailError={userEmailError} contextLogLabelText={I18n.get('reasonForOverride')} />
        </Modal.Body>
        <Modal.Footer>
          <BasicButton className="btn btn-secondary" onClick={onCancel} label={I18n.get('cancel')} />
          <BasicButton title={I18n.get('confirmAction', {action: I18n.get('overrideValue')})} className="btn btn-primary" onClick={() => handleConfirm()} label={I18n.get('overrideValue')} />
        </Modal.Footer>
      </Modal>
    </>
  );
}
